import { useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import Checkout from './pages/Checkout';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Users from './pages/Users';
import Upload from './pages/Upload';
import CreateRedirect from './pages/CreateRedirect';
import MediaBuilder from './pages/MediaBuilder';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Contacts from './pages/Contacts';
import WhatsAppTemplates from './pages/WhatsAppTemplates';
import Support from './pages/Support';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import ForgotPassword from './pages/ForgotPassword';
import NotFound from './pages/NotFound';
import Onboarding from './pages/Onboarding';
import AppEntry from './pages/AppEntry';
import Signup from './pages/Signup';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import AdminPlans from './pages/admin/Plans';
import AdminUsers from './pages/admin/Users';
import AdminUsage from './pages/admin/Usage';
import AdminGenerationPricing from './pages/admin/GenerationPricing';
import ProtectedRoute from './components/ProtectedRoute';
import WorkspaceLayout from './components/WorkspaceLayout';
import RequireRole from './components/RequireRole';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import SubscriptionGate from './components/SubscriptionGate';
import ImpersonationBanner from './components/ImpersonationBanner';
import { getAccessToken, subscribeSession } from './lib/session';
import './i18n/config';

const queryClient = new QueryClient();

const App = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // When a session ends — sign-out, or a refresh that found it over — nothing cached under it may
  // be shown to whoever signs in next on this browser.
  useEffect(() => subscribeSession(() => {
    if (!getAccessToken()) queryClient.clear();
  }), []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ImpersonationBanner />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/demo" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/support" element={<Support />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />

          {/* Auth, workspace-agnostic */}
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/app" element={<ProtectedRoute><AppEntry /></ProtectedRoute>} />
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />

          {/* Platform admin (super-admin only) */}
          <Route path="/admin/plans" element={<ProtectedRoute><RequireSuperAdmin><AdminPlans /></RequireSuperAdmin></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><RequireSuperAdmin><AdminUsers /></RequireSuperAdmin></ProtectedRoute>} />
          <Route path="/admin/usage" element={<ProtectedRoute><RequireSuperAdmin><AdminUsage /></RequireSuperAdmin></ProtectedRoute>} />
          <Route path="/admin/generation-pricing" element={<ProtectedRoute><RequireSuperAdmin><AdminGenerationPricing /></RequireSuperAdmin></ProtectedRoute>} />

          {/* Workspace-scoped */}
          <Route path="/w/:workspaceId" element={<ProtectedRoute><WorkspaceLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            {/* Campaigns is one page with channel tabs: making the creative and sending it are one
                job. The tab lives in the path rather than in component state, so it is a real
                location — linkable, bookmarkable, and on the back stack. React Router ranks a
                static segment above a param, so `media`/`whatsapp` always beat `:campaignId` no
                matter what order these lines are in; a campaign id can never be shadowed by a tab
                name, and a tab name can never fall through to CampaignDetail. */}
            <Route path="campaigns">
              {/* `replace`, or Back from the whatsapp tab lands here and redirects forward again. */}
              <Route index element={<Navigate to="whatsapp" replace />} />
              <Route path="whatsapp" element={<RequireRole permission="campaign:read"><Campaigns tab="whatsapp" /></RequireRole>} />
              <Route path="media" element={<RequireRole permission="campaign:read"><Campaigns tab="media" /></RequireRole>} />
              {/* The builder is a full page, not a tab: it is an editor, and it is the one thing in
                  this block behind SubscriptionGate. Browsing what you already made stays available
                  when a subscription lapses; generating more does not. */}
              <Route path="media/:mediaId" element={<RequireRole permission="campaign:update"><SubscriptionGate><MediaBuilder /></SubscriptionGate></RequireRole>} />
              {/* Step 1 of the setup stepper for a campaign that does not exist yet — the row is
                  created when that step is submitted. Behind SubscriptionGate because it only leads
                  to a create; the static segment outranks `:campaignId` below. */}
              <Route path="new" element={<RequireRole permission="campaign:create"><SubscriptionGate><CampaignDetail isNew /></SubscriptionGate></RequireRole>} />
              {/* One page, one stepper: setup steps before launch, results after. Guarded on
                  campaign:read (not campaign:launch — a viewer must be able to see what happened;
                  the editor's own controls are gated separately), and deliberately NOT behind
                  SubscriptionGate: a workspace whose subscription lapsed must still be able to see
                  what its last campaign did. */}
              <Route path=":campaignId" element={<RequireRole permission="campaign:read"><CampaignDetail /></RequireRole>} />
            </Route>
            <Route path="billing" element={<RequireRole permission="billing:read"><Billing /></RequireRole>} />
            <Route path="contacts" element={<RequireRole permission="contacts:read"><SubscriptionGate><Contacts /></SubscriptionGate></RequireRole>} />
            <Route path="create" element={<CreateRedirect />} />
            <Route path="upload" element={<RequireRole permission="uploads:create"><SubscriptionGate><Upload /></SubscriptionGate></RequireRole>} />
            <Route path="whatsapp-templates" element={<RequireRole permission="templates:read"><SubscriptionGate><WhatsAppTemplates /></SubscriptionGate></RequireRole>} />
            <Route path="users" element={<RequireRole permission="members:read"><Users /></RequireRole>} />
          </Route>

          {/* Legacy redirects */}
          <Route path="/dashboard" element={<Navigate to="/app?next=dashboard" replace />} />
          <Route path="/contacts" element={<Navigate to="/app?next=contacts" replace />} />
          <Route path="/create" element={<Navigate to="/app?next=create" replace />} />
          <Route path="/upload" element={<Navigate to="/app?next=upload" replace />} />
          <Route path="/users" element={<Navigate to="/app?next=users" replace />} />
          <Route path="/whatsapp-templates" element={<Navigate to="/app?next=whatsapp-templates" replace />} />

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
